import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserInvitations } from '@/hooks/useInvitations';
import { useAllGuests, useCreateGuest, useUpdateGuest, useDeleteGuest, useMarkLinkSent, Guest } from '@/hooks/useGuests';
import { confirmedAttendees, pendingAttendees, expectedAttendees } from '@/lib/guests/headcount';
import { useEvent } from '@/hooks/useEvent';
import { parseCalendarDate } from '@/lib/parseCalendarDate';
import { buildWhatsAppMessage } from '@/lib/invitation/buildWhatsAppMessage';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Users,
  Plus,
  Search,
  ArrowLeft,
  UserCheck,
  UserX,
  Clock,
  Trash2,
  Mail,
  Phone,
  Copy,
  ExternalLink,
  Send,
  UtensilsCrossed,
  MessageSquare,
  Eye,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import CSVImportModal from '@/components/dashboard/CSVImportModal';

const GROUP_TYPES = [
  { value: 'familia_novio', label: 'Familia Novio' },
  { value: 'familia_novia', label: 'Familia Novia' },
  { value: 'amigos_novio', label: 'Amigos Novio' },
  { value: 'amigos_novia', label: 'Amigos Novia' },
  { value: 'trabajo', label: 'Trabajo' },
  { value: 'otros', label: 'Otros' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'confirmed', label: 'Confirmados' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'declined', label: 'Rechazados' },
  // 008 · US3 — seguimiento de envío
  { value: 'not_sent', label: 'No enviados' },
  { value: 'sent_no_reply', label: 'Enviados sin responder' },
];

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'confirmed':
      return <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border border-green-200">Confirmado</Badge>;
    case 'declined':
      return <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border border-red-200">Rechazado</Badge>;
    default:
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border border-amber-200">Pendiente</Badge>;
  }
};

const getGroupLabel = (value: string) => {
  return GROUP_TYPES.find(g => g.value === value)?.label || value;
};

const Guests = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: invitations = [] } = useUserInvitations();
  const { data: guests = [], isLoading: guestsLoading } = useAllGuests();
  const { data: event } = useEvent();
  // T020 (US1): el conteo queda "cerrado" cuando la fecha límite de RSVP ya venció.
  const rsvpClosed = !!event?.rsvp_deadline && new Date(event.rsvp_deadline) < new Date();
  
  const createGuest = useCreateGuest();
  const updateGuest = useUpdateGuest();
  const deleteGuest = useDeleteGuest();
  const markLinkSent = useMarkLinkSent();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<(Guest & { invitationName: string; invitationSlug: string; invitationPublished: boolean }) | null>(null);
  const [isNewGuest, setIsNewGuest] = useState(false);

  const [guestForm, setGuestForm] = useState({
    invitation_id: '',
    guest_name: '',
    display_label: '',
    guest_email: '',
    guest_phone: '',
    group_type: 'otros',
    table_number: '',
    passes_count: '1',
    rsvp_status: 'pending',
    rsvp_guests_count: '',
    rsvp_dietary_restrictions: '',
    rsvp_message: '',
  });

  // Filter guests
  const filteredGuests = useMemo(() => {
    return guests.filter((guest) => {
      const matchesSearch = guest.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.guest_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.guest_phone?.includes(searchTerm);
      
      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'not_sent'
            ? !guest.link_sent_at
            : statusFilter === 'sent_no_reply'
              ? !!guest.link_sent_at && guest.rsvp_status === 'pending'
              : guest.rsvp_status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [guests, searchTerm, statusFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = guests.length;
    const confirmed = guests.filter(g => g.rsvp_status === 'confirmed').length;
    const pending = guests.filter(g => g.rsvp_status === 'pending').length;
    const declined = guests.filter(g => g.rsvp_status === 'declined').length;
    const totalAttendees = confirmedAttendees(guests);
    // Asistentes por confirmar (pases de los pendientes) y global (sin rechazados).
    const pendingAtt = pendingAttendees(guests);
    const expectedAtt = expectedAttendees(guests);
    const confirmationRate = total > 0 ? Math.round((confirmed / total) * 100) : 0;

    // 008 · SC-008 (métrica de éxito) — tasa de respuesta, % vistos, % con rótulo.
    const sent = guests.filter(g => !!g.link_sent_at).length;
    const responded = guests.filter(g => g.rsvp_status === 'confirmed' || g.rsvp_status === 'declined').length;
    const viewed = guests.filter(g => !!g.first_viewed_at).length;
    const withLabel = guests.filter(g => !!g.display_label).length;
    const responseRate = sent > 0 ? Math.round((responded / sent) * 100) : null;
    const viewedRate = total > 0 ? Math.round((viewed / total) * 100) : 0;
    const labelRate = total > 0 ? Math.round((withLabel / total) * 100) : 0;

    return {
      total, confirmed, pending, declined, totalAttendees, confirmationRate,
      pendingAttendees: pendingAtt, expectedAttendees: expectedAtt,
      responseRate, viewedRate, labelRate, sent,
    };
  }, [guests]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    navigate('/auth');
    return null;
  }

  const openNewGuest = () => {
    setIsNewGuest(true);
    setSelectedGuest(null);
    setGuestForm({
      invitation_id: invitations[0]?.id || '',
      guest_name: '',
      display_label: '',
      guest_email: '',
      guest_phone: '',
      group_type: 'otros',
      table_number: '',
      passes_count: '1',
      rsvp_status: 'pending',
      rsvp_guests_count: '',
      rsvp_dietary_restrictions: '',
      rsvp_message: '',
    });
    setSheetOpen(true);
  };

  const openEditGuest = (guest: Guest & { invitationName: string; invitationSlug: string; invitationPublished: boolean }) => {
    setIsNewGuest(false);
    setSelectedGuest(guest);
    setGuestForm({
      invitation_id: guest.invitation_id,
      guest_name: guest.guest_name,
      display_label: guest.display_label || '',
      guest_email: guest.guest_email || '',
      guest_phone: guest.guest_phone || '',
      group_type: guest.group_type || 'otros',
      table_number: guest.table_number?.toString() || '',
      passes_count: guest.passes_count?.toString() || '1',
      rsvp_status: guest.rsvp_status || 'pending',
      rsvp_guests_count: guest.rsvp_guests_count?.toString() || '',
      rsvp_dietary_restrictions: guest.rsvp_dietary_restrictions || '',
      rsvp_message: guest.rsvp_message || '',
    });
    setSheetOpen(true);
  };

  const copyInviteLink = (guest: Guest & { invitationSlug: string }) => {
    const link = `${window.location.origin}/i/${guest.invitationSlug}?code=${guest.unique_access_code}`;
    navigator.clipboard.writeText(link);
    markLinkSent.mutate(guest.id); // 008 · US3 — copiar cuenta como envío
    toast.success('Link de invitación copiado');
  };

  const openInvitation = (guest: Guest & { invitationSlug: string }) => {
    window.open(`/i/${guest.invitationSlug}?code=${guest.unique_access_code}`, '_blank');
  };

  const shareViaWhatsApp = (guest: Guest & { invitationSlug: string }) => {
    const link = `${window.location.origin}/i/${guest.invitationSlug}?code=${guest.unique_access_code}`;
    // 008 · US6 — mensaje configurable con datos del evento (nombres en el orden elegido).
    const novios = event
      ? event.couple_name_order === 'groom_first'
        ? `${event.groom_name ?? ''} y ${event.bride_name ?? ''}`
        : `${event.bride_name ?? ''} y ${event.groom_name ?? ''}`
      : '';
    const fecha = event?.wedding_date
      ? (parseCalendarDate(event.wedding_date) ?? new Date(event.wedding_date)).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })
      : undefined;
    const message = buildWhatsAppMessage(
      { invitado: guest.display_label || guest.guest_name, novios, fecha, link },
      event?.whatsapp_message_template,
    );
    window.open(`https://wa.me/${guest.guest_phone?.replace(/\D/g, '') || ''}?text=${encodeURIComponent(message)}`, '_blank');
    markLinkSent.mutate(guest.id); // 008 · US3 — enviar por WhatsApp cuenta como envío
  };

  const handleSave = () => {
    const data = {
      invitation_id: guestForm.invitation_id,
      guest_name: guestForm.guest_name,
      display_label: guestForm.display_label.trim() || null,
      guest_email: guestForm.guest_email || undefined,
      guest_phone: guestForm.guest_phone || undefined,
      group_type: guestForm.group_type,
      table_number: guestForm.table_number ? parseInt(guestForm.table_number) : undefined,
      passes_count: parseInt(guestForm.passes_count) || 1,
      rsvp_status: guestForm.rsvp_status as 'pending' | 'confirmed' | 'declined',
      rsvp_guests_count: guestForm.rsvp_guests_count ? parseInt(guestForm.rsvp_guests_count) : undefined,
      rsvp_dietary_restrictions: guestForm.rsvp_dietary_restrictions || undefined,
      rsvp_message: guestForm.rsvp_message || undefined,
    };

    if (isNewGuest) {
      createGuest.mutate(data);
    } else if (selectedGuest) {
      // T019 (US1/GAP-6): proteger el RSVP del invitado. Si el invitado ya respondió y la
      // pareja cambia su estado/conteo, pedir confirmación explícita y registrar el origen.
      const guestResponded =
        selectedGuest.rsvp_last_source === 'guest' ||
        (!selectedGuest.rsvp_last_source && !!selectedGuest.rsvp_responded_at);
      const rsvpChanged =
        data.rsvp_status !== selectedGuest.rsvp_status ||
        (data.rsvp_guests_count ?? null) !== (selectedGuest.rsvp_guests_count ?? null);

      if (guestResponded && rsvpChanged) {
        const ok = window.confirm(
          'Este invitado ya respondió. ¿Seguro que quieres cambiar su RSVP? ' +
            'Quedará registrado que el cambio lo hiciste tú (no el invitado).',
        );
        if (!ok) return; // no guarda ni cierra el panel
      }

      updateGuest.mutate({
        id: selectedGuest.id,
        ...data,
        ...(rsvpChanged
          ? { rsvp_last_source: 'couple' as const, rsvp_last_edited_at: new Date().toISOString() }
          : {}),
      });
    }

    setSheetOpen(false);
  };

  const handleDelete = () => {
    if (selectedGuest) {
      deleteGuest.mutate(selectedGuest.id);
      setSheetOpen(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <DashboardSidebar />
      
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-serif text-2xl md:text-3xl font-medium text-foreground">
                  Gestión de Invitados
                </h1>
                <p className="text-muted-foreground text-sm">
                  Administra tu lista de invitados y confirmaciones
                </p>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="bg-gradient-to-br from-primary/10 to-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">% Confirmación</p>
                    <p className="font-serif text-3xl font-medium text-primary">
                      {stats.confirmationRate}%
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-medium">{stats.confirmed}</p>
                    {rsvpClosed && (
                      <Badge variant="secondary" className="text-[10px]">Cerrado</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Confirmados · {stats.totalAttendees} asistentes
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-medium">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">
                    Pendientes · {stats.pendingAttendees} asistentes
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <UserX className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <p className="text-2xl font-medium">{stats.declined}</p>
                  <p className="text-xs text-muted-foreground">Rechazados</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 008 · SC-008 — Métricas de la feature */}
          <Card className="bg-muted/30">
            <CardContent className="py-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <span>
                <span className="text-muted-foreground">Asistentes esperados (confirmados + pendientes): </span>
                <span className="font-medium text-primary">{stats.expectedAttendees}</span>
              </span>
              <span>
                <span className="text-muted-foreground">Tasa de respuesta: </span>
                <span className="font-medium">{stats.responseRate !== null ? `${stats.responseRate}%` : '—'}</span>
                <span className="text-xs text-muted-foreground"> ({stats.sent} enviados)</span>
              </span>
              <span>
                <span className="text-muted-foreground">Vieron la invitación: </span>
                <span className="font-medium">{stats.viewedRate}%</span>
              </span>
              <span>
                <span className="text-muted-foreground">Con rótulo: </span>
                <span className="font-medium">{stats.labelRate}%</span>
              </span>
            </CardContent>
          </Card>

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-1 gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, email o teléfono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setImportOpen(true)}
                disabled={invitations.length === 0}
              >
                <Upload className="w-4 h-4 mr-2" />
                Importar CSV
              </Button>
              <Button onClick={openNewGuest} disabled={invitations.length === 0}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Invitado
              </Button>
            </div>
          </div>

          {/* Guests Table */}
          <Card>
            <CardContent className="p-0">
              {invitations.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground mb-2">Primero crea una invitación</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Los invitados están asociados a invitaciones específicas
                  </p>
                  <Button onClick={() => navigate('/invitations/new')}>
                    Crear Invitación
                  </Button>
                </div>
              ) : guestsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : filteredGuests.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'No se encontraron invitados con esos filtros'
                      : 'Aún no has agregado invitados'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <TooltipProvider>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nombre Completo</TableHead>
                          <TableHead>Contacto</TableHead>
                          <TableHead>Grupo</TableHead>
                          <TableHead className="text-center">Mesa</TableHead>
                          <TableHead className="text-center">Pases</TableHead>
                          <TableHead className="text-center">Confirmados</TableHead>
                          <TableHead className="text-center">Estado</TableHead>
                          <TableHead className="text-center">Info</TableHead>
                          <TableHead className="text-center">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredGuests.map((guest) => (
                          <TableRow 
                            key={guest.id} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => openEditGuest(guest)}
                          >
                            <TableCell>
                              <div>
                                <p className="font-medium flex items-center gap-1.5">
                                  {guest.guest_name}
                                  {guest.first_viewed_at && (
                                    <Eye className="w-3.5 h-3.5 text-muted-foreground" aria-label="Vio la invitación" />
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground">{guest.invitationName}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {guest.guest_email && (
                                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                    <Mail className="w-3 h-3" />
                                    {guest.guest_email}
                                  </div>
                                )}
                                {guest.guest_phone && (
                                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                    <Phone className="w-3 h-3" />
                                    {guest.guest_phone}
                                  </div>
                                )}
                                {!guest.guest_email && !guest.guest_phone && (
                                  <span className="text-sm text-muted-foreground">-</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{getGroupLabel(guest.group_type)}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              {guest.table_number || '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              {guest.passes_count}
                            </TableCell>
                            <TableCell className="text-center">
                              {guest.rsvp_status === 'confirmed' ? (
                                <span className="font-medium text-secondary">{guest.rsvp_guests_count || 0}</span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                {getStatusBadge(guest.rsvp_status)}
                                {guest.link_sent_at ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center gap-1 text-[10px] text-secondary cursor-help">
                                        <Send className="w-3 h-3" /> Enviado
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">
                                        Enviado el {new Date(guest.link_sent_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground">Sin enviar</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                                {guest.rsvp_dietary_restrictions && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="p-1.5 rounded bg-amber-100 text-amber-700 cursor-help">
                                        <UtensilsCrossed className="w-3.5 h-3.5" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p className="text-xs font-medium mb-1">Restricción alimentaria:</p>
                                      <p className="text-xs">{guest.rsvp_dietary_restrictions}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {guest.rsvp_message && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="p-1.5 rounded bg-primary/10 text-primary cursor-help">
                                        <MessageSquare className="w-3.5 h-3.5" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p className="text-xs font-medium mb-1">Mensaje:</p>
                                      <p className="text-xs italic">"{guest.rsvp_message}"</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {!guest.rsvp_dietary_restrictions && !guest.rsvp_message && (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </div>
                            </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => copyInviteLink(guest)}
                                title="Copiar link"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              {guest.invitationPublished && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => openInvitation(guest)}
                                  title="Ver invitación"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              )}
                              {guest.guest_phone && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-green-600"
                                  onClick={() => shareViaWhatsApp(guest)}
                                  title="Enviar por WhatsApp"
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TooltipProvider>
              </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Side Sheet for Edit/Create */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{isNewGuest ? 'Nuevo Invitado' : 'Editar Invitado'}</SheetTitle>
            <SheetDescription>
              {isNewGuest 
                ? 'Agrega un nuevo invitado a tu lista'
                : 'Modifica los datos del invitado o cambia su estado'}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-6">
            {invitations.length > 1 && (
              <div>
                <Label>Invitación</Label>
                <Select 
                  value={guestForm.invitation_id} 
                  onValueChange={(v) => setGuestForm({ ...guestForm, invitation_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar invitación" />
                  </SelectTrigger>
                  <SelectContent>
                    {invitations.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.bride_name} & {inv.groom_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Nombre Completo *</Label>
              <Input
                placeholder="Juan Pérez"
                value={guestForm.guest_name}
                onChange={(e) => setGuestForm({ ...guestForm, guest_name: e.target.value })}
              />
            </div>

            <div>
              <Label>Rótulo de invitación</Label>
              <Input
                placeholder="Juan Pérez y esposa"
                value={guestForm.display_label}
                onChange={(e) => setGuestForm({ ...guestForm, display_label: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Cómo se dirige la invitación a este invitado. Si lo dejas vacío, se usa el nombre.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@ejemplo.com"
                  value={guestForm.guest_email}
                  onChange={(e) => setGuestForm({ ...guestForm, guest_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  type="tel"
                  placeholder="+52 555 123 4567"
                  value={guestForm.guest_phone}
                  onChange={(e) => setGuestForm({ ...guestForm, guest_phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Grupo</Label>
              <Select 
                value={guestForm.group_type} 
                onValueChange={(v) => setGuestForm({ ...guestForm, group_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GROUP_TYPES.map((group) => (
                    <SelectItem key={group.value} value={group.value}>
                      {group.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mesa Asignada</Label>
                <Input
                  type="number"
                  placeholder="1"
                  value={guestForm.table_number}
                  onChange={(e) => setGuestForm({ ...guestForm, table_number: e.target.value })}
                />
              </div>
              <div>
                <Label>Número de Pases</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="1"
                  value={guestForm.passes_count}
                  onChange={(e) => setGuestForm({ ...guestForm, passes_count: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estado RSVP</Label>
                <Select 
                  value={guestForm.rsvp_status} 
                  onValueChange={(v) => setGuestForm({ ...guestForm, rsvp_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        Pendiente
                      </div>
                    </SelectItem>
                    <SelectItem value="confirmed">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-secondary" />
                        Confirmado
                      </div>
                    </SelectItem>
                    <SelectItem value="declined">
                      <div className="flex items-center gap-2">
                        <UserX className="w-4 h-4 text-destructive" />
                        Rechazado
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {guestForm.rsvp_status === 'confirmed' && (
                <div>
                  <Label>Personas Confirmadas</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={guestForm.rsvp_guests_count}
                    onChange={(e) => setGuestForm({ ...guestForm, rsvp_guests_count: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* 008 · US5 — campos del invitado. En alta no se editan (los aporta el invitado);
                en edición: restricciones editable (dato de catering), mensaje solo lectura. */}
            {!isNewGuest && (
              <div>
                <Label className="flex items-center gap-2">
                  <UtensilsCrossed className="w-4 h-4 text-amber-600" />
                  Restricciones Alimentarias
                </Label>
                <Input
                  placeholder="Ej: Vegetariano, sin gluten, alergia a mariscos..."
                  value={guestForm.rsvp_dietary_restrictions}
                  onChange={(e) => setGuestForm({ ...guestForm, rsvp_dietary_restrictions: e.target.value })}
                />
              </div>
            )}

            {!isNewGuest && (
              <div>
                <Label className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  Mensaje del invitado
                </Label>
                <Textarea
                  placeholder="Lo escribe el invitado al confirmar."
                  value={guestForm.rsvp_message}
                  readOnly
                  rows={3}
                  className="bg-muted/50"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Este mensaje lo escribe el invitado; no es editable por ustedes.
                </p>
              </div>
            )}

            {selectedGuest && (
              <div className="pt-4 border-t space-y-3">
                <p className="text-xs text-muted-foreground">
                  Código de acceso: <code className="bg-muted px-2 py-1 rounded">{selectedGuest.unique_access_code}</code>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyInviteLink(selectedGuest)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Link
                  </Button>
                  {selectedGuest.invitationPublished && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openInvitation(selectedGuest)}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ver Invitación
                    </Button>
                  )}
                  {selectedGuest.guest_phone && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      onClick={() => shareViaWhatsApp(selectedGuest)}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <SheetFooter className="flex-col sm:flex-row gap-2">
            {!isNewGuest && selectedGuest && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full sm:w-auto">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar invitado?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta acción no se puede deshacer.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button 
              onClick={handleSave} 
              className="w-full sm:w-auto sm:ml-auto"
              disabled={!guestForm.guest_name || !guestForm.invitation_id}
            >
              {isNewGuest ? 'Agregar Invitado' : 'Guardar Cambios'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <CSVImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        invitations={invitations.map((inv) => ({
          id: inv.id,
          bride_name: inv.bride_name,
          groom_name: inv.groom_name,
        }))}
      />
    </div>
  );
};

export default Guests;
